import React, { useEffect, useState, Fragment } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import { Typography } from "@mui/material";

import { schoolList } from "../utils/config";
import states from "../utils/states.json";
import getLink from "../utils/getLink";

type FormInfoProps = {
    submitted: boolean;
    toggleSubmitted: () => void;
    setLink: (link: string) => void;
};

export default function FormInfo(props: FormInfoProps) {
    const [checked, setChecked] = useState(true);

    const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        props.toggleSubmitted();
        const data = new FormData(e.currentTarget);
        const link = getLink(data, school, checked);
        props.setLink(link);
        window.open(link);
    };

    const [school, setSchool] = useState("");
    const handleSchoolChange = (e: any) => {
        setSchool(e.target.value);
    };

    const [parentState, setParentState] = useState("");
    const handleParentStateChange = (e: any) => {
        setParentState(e.target.value);
    };

    const [studentState, setStudentState] = useState("");
    const handleStudentStateChange = (e: any) => {
        setStudentState(e.target.value);
    };

    const [phoneNum, setPhoneNum] = useState("");
    const handlePhoneChange = (e: any) => {
        const onlyNums = e.target.value.replace(/[^0-9]/g, "");
        if (onlyNums.length < 4) {
            setPhoneNum(onlyNums);
        } else if (onlyNums.length < 7) {
            const number = onlyNums.replace(/(\d{3})(\d{0,})/, "($1) $2");
            setPhoneNum(number);
        } else if (onlyNums.length <= 10) {
            const number = onlyNums.replace(
                /(\d{3})(\d{3})(\d{0,})/,
                "($1) $2-$3"
            );
            setPhoneNum(number);
        }
    };

    const [parentPostalCode, setParentPostalCode] = useState("");
    const handleParentPostalCodeChange = (e: any) => {
        const onlyNums = e.target.value.replace(/[^0-9]/g, "");
        if (onlyNums.length <= 5) {
            setParentPostalCode(onlyNums);
        }
    };
    const [studentPostalCode, setStudentPostalCode] = useState("");
    const handleStudentPostalCodeChange = (e: any) => {
        const onlyNums = e.target.value.replace(/[^0-9]/g, "");
        if (onlyNums.length <= 5) {
            setStudentPostalCode(onlyNums);
        }
    };

    const [email, setEmail] = useState("");
    const [emailErrMsg, setEmailErrMsg] = useState("Hi");
    const handleEmailChange = (e: any) => {
        setEmail(e.target.value);
    };

    useEffect(() => {
        setEmailErrMsg(
            email === ""
                ? ""
                : !email.match(
                      /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
                  )
                ? "Must be a valid email"
                : ""
        );
    }, [email]);

    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        function handleResize() {
            setWidth(window.innerWidth);
        }

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [width]);

    return (
        <Fragment>
            <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="body2">
                    Please fill out this form. On completion, you will be
                    redirected to a DocuSign page where you can complete the
                    signing process.
                </Typography>
                <Typography variant="h5" my={2} color="primary">
                    School Info
                </Typography>
                <Grid item xs={11} sm={6}>
                    <TextField
                        label="School Name"
                        required
                        style={{ maxWidth: width - 80 }}
                        id="school_name"
                        name="school_name"
                        value={school}
                        onChange={handleSchoolChange}
                        select
                        fullWidth
                    >
                        {schoolList.sort().map((school, i) => (
                            <MenuItem value={school} key={i}>
                                {school}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Typography variant="h5" mb={2} mt={4} color="primary">
                    Student Info
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="student_birthdate"
                            required
                            fullWidth
                            id="student_birthdate"
                            label="Birth Date"
                            defaultValue="2006-01-01"
                            type="date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={8} />
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="student_first"
                            required
                            fullWidth
                            id="student_first"
                            label="First Name"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            id="student_middle"
                            label="Middle Name"
                            name="student_middle"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required
                            fullWidth
                            id="student_last"
                            label="Last Name"
                            name="student_last"
                        />
                    </Grid>
                </Grid>
                <Typography variant="h6" my={2}>
                    Home Address
                </Typography>
                <Box mb={1} mt={0}>
                    Same as Parent/Guardian
                    <Checkbox
                        checked={checked}
                        onClick={() => {
                            setChecked(!checked);
                        }}
                    />
                </Box>
                {!checked ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                name="student_street"
                                required
                                fullWidth
                                id="student_street"
                                label="Street Address"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                required
                                fullWidth
                                id="student_city"
                                label="City"
                                name="student_city"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                required
                                fullWidth
                                id="student_state"
                                label="State"
                                name="student_state"
                                onChange={handleStudentStateChange}
                                value={studentState}
                                select
                            >
                                {states.sort().map((state, i) => (
                                    <MenuItem value={state} key={i}>
                                        {state}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                required
                                fullWidth
                                id="student_postal_code"
                                label="Postal Code"
                                name="student_postal_code"
                                onChange={handleStudentPostalCodeChange}
                                value={studentPostalCode}
                            />
                        </Grid>
                    </Grid>
                ) : null}
                <Typography variant="h5" mb={2} mt={4} color="primary">
                    Parent/Guardian Info
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="parent_first"
                            required
                            fullWidth
                            id="parent_first"
                            label="First Name"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            id="parent_middle"
                            label="Middle Name"
                            name="parent_middle"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required
                            fullWidth
                            id="parent_last"
                            label="Last Name"
                            name="parent_last"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            id="parent_email"
                            label="Email Address"
                            name="parent_email"
                            onChange={handleEmailChange}
                            error={emailErrMsg !== ""}
                            helperText={emailErrMsg}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            id="parent_phone"
                            label="Phone Number"
                            name="parent_phone"
                            onChange={handlePhoneChange}
                            value={phoneNum}
                        />
                    </Grid>
                </Grid>
                <Typography variant="h6" my={2}>
                    Home Address
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            name="parent_street"
                            required
                            fullWidth
                            id="parent_street"
                            label="Street Address"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required
                            fullWidth
                            id="parent_city"
                            label="City"
                            name="parent_city"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required
                            fullWidth
                            id="parent_state"
                            label="State"
                            name="parent_state"
                            onChange={handleParentStateChange}
                            value={parentState}
                            select
                        >
                            {states.sort().map((state, i) => (
                                <MenuItem value={state} key={i}>
                                    {state}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required
                            fullWidth
                            id="parent_postal_code"
                            label="Postal Code"
                            name="parent_postal_code"
                            onChange={handleParentPostalCodeChange}
                            value={parentPostalCode}
                        />
                    </Grid>
                </Grid>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <p>
                        After clicking below, you will be redirected to DocuSign
                        to complete this form.
                    </p>
                    <Button type="submit" variant="contained" sx={{ mt: 3 }}>
                        <Typography color={"neutral.white"}>
                            Continue in DocuSign
                        </Typography>
                    </Button>
                </Box>
            </Box>
        </Fragment>
    );
}
